import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import API_BASE_URL from "../config/api";

interface User {
  id: string;
  email: string;
  name: string;
  avatar?: string;
  category: string;
  isVerified: boolean;
}

interface UsageOverview {
  planType: string;
  apiRequests: { used: number; limit: number | null };
  tokens: { used: number; limit: number | null };
}

interface RecentActivity {
  id: string;
  type: 'chat' | 'script' | 'api' | 'subscription';
  title: string;
  description: string;
  timestamp: string;
  icon: string;
}

// ✅ NEW: Usage Insights Component
const UsageInsights = ({ usageOverview }: { usageOverview: UsageOverview }) => {
  const getUsageStatus = (used: number, limit: number | null) => {
    if (!limit) return { status: 'unlimited', color: 'from-blue-400 to-cyan-500', icon: '♾️' };
    const percentage = (used / limit) * 100;
    if (percentage > 90) return { status: 'critical', color: 'from-red-400 to-pink-500', icon: '⚠️' };
    if (percentage > 75) return { status: 'warning', color: 'from-yellow-400 to-orange-500', icon: '⚡' };
    return { status: 'healthy', color: 'from-green-400 to-emerald-500', icon: '✅' };
  };

  const apiStatus = getUsageStatus(usageOverview.apiRequests.used, usageOverview.apiRequests.limit);
  const tokenStatus = getUsageStatus(usageOverview.tokens.used, usageOverview.tokens.limit);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* API Usage Insight */}
      <div className="bg-white/70 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 overflow-hidden group hover:shadow-2xl transition-all duration-300">
        <div className={`bg-gradient-to-r ${apiStatus.color} p-6 text-white`}>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold">API Usage</h3>
              <p className="text-white/90 text-sm">This month's activity</p>
            </div>
            <div className="text-3xl">{apiStatus.icon}</div>
          </div>
        </div>
        <div className="p-6">
          <div className="flex items-baseline space-x-2 mb-3">
            <span className="text-3xl font-bold text-gray-800">
              {usageOverview.apiRequests.used.toLocaleString()}
            </span>
            <span className="text-gray-500 text-sm">
              {usageOverview.apiRequests.limit ? `of ${usageOverview.apiRequests.limit.toLocaleString()}` : 'unlimited'}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
            <div
              className={`h-3 rounded-full bg-gradient-to-r ${apiStatus.color} transition-all duration-500`}
              style={{
                width: `${usageOverview.apiRequests.limit ? Math.min((usageOverview.apiRequests.used / usageOverview.apiRequests.limit) * 100, 100) : 100}%`,
              }}
            />
          </div>
          <p className="text-sm text-gray-600 mt-2">
            Status: <span className="font-medium capitalize">{apiStatus.status}</span>
          </p>
        </div>
      </div>

      {/* Token Usage Insight */}
      <div className="bg-white/70 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 overflow-hidden group hover:shadow-2xl transition-all duration-300">
        <div className={`bg-gradient-to-r ${tokenStatus.color} p-6 text-white`}>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold">Token Usage</h3>
              <p className="text-white/90 text-sm">Processing power used</p>
            </div>
            <div className="text-3xl">{tokenStatus.icon}</div>
          </div>
        </div>
        <div className="p-6">
          <div className="flex items-baseline space-x-2 mb-3">
            <span className="text-3xl font-bold text-gray-800">
              {usageOverview.tokens.used.toLocaleString()}
            </span>
            <span className="text-gray-500 text-sm">
              {usageOverview.tokens.limit ? `of ${usageOverview.tokens.limit.toLocaleString()}` : 'unlimited'}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
            <div
              className={`h-3 rounded-full bg-gradient-to-r ${tokenStatus.color} transition-all duration-500`}
              style={{
                width: `${usageOverview.tokens.limit ? Math.min((usageOverview.tokens.used / usageOverview.tokens.limit) * 100, 100) : 100}%`,
              }}
            />
          </div>
          <p className="text-sm text-gray-600 mt-2">
            Status: <span className="font-medium capitalize">{tokenStatus.status}</span>
          </p>
        </div>
      </div>
    </div>
  );
};

// ✅ NEW: Status Cards Component
const StatusCards = ({ user, usageOverview }: { user: User | null; usageOverview: UsageOverview | null }) => {
  const currentTime = new Date();
  const greeting = currentTime.getHours() < 12 ? 'Good Morning' : 
                  currentTime.getHours() < 18 ? 'Good Afternoon' : 'Good Evening';

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {/* Welcome Card */}
      <div className="bg-gradient-to-br from-indigo-500 to-purple-600 text-white rounded-2xl p-6 shadow-xl">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
            <span className="text-2xl">👋</span>
          </div>
          <div>
            <h3 className="font-bold text-lg">{greeting}!</h3>
            <p className="text-indigo-100 text-sm">Welcome back to your dashboard</p>
          </div>
        </div>
        <div className="bg-white/10 rounded-xl p-3">
          <p className="text-sm text-indigo-100">Account Status</p>
          <p className="font-semibold flex items-center">
            <span className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></span>
            {user?.isVerified ? 'Verified & Active' : 'Pending Verification'}
          </p>
        </div>
      </div>

      {/* Plan Status Card */}
      <div className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white rounded-2xl p-6 shadow-xl">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
            <span className="text-2xl">💎</span>
          </div>
          <div>
            <h3 className="font-bold text-lg">Current Plan</h3>
            <p className="text-emerald-100 text-sm">Your subscription tier</p>
          </div>
        </div>
        <div className="bg-white/10 rounded-xl p-3">
          <p className="text-sm text-emerald-100">Plan Type</p>
          <p className="font-semibold text-xl">
            {usageOverview?.planType || 'Loading...'}
          </p>
        </div>
      </div>

      {/* Today's Date & Activity */}
      <div className="bg-gradient-to-br from-orange-500 to-red-600 text-white rounded-2xl p-6 shadow-xl">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
            <span className="text-2xl">📅</span>
          </div>
          <div>
            <h3 className="font-bold text-lg">Today</h3>
            <p className="text-orange-100 text-sm">Current date & time</p>
          </div>
        </div>
        <div className="bg-white/10 rounded-xl p-3">
          <p className="text-sm text-orange-100">Date</p>
          <p className="font-semibold">
            {currentTime.toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
          <p className="text-sm text-orange-200 mt-1">
            {currentTime.toLocaleTimeString('en-US', { 
              hour: '2-digit', 
              minute: '2-digit',
              hour12: true 
            })}
          </p>
        </div>
      </div>
    </div>
  );
};

const Dashboard: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [usageOverview, setUsageOverview] = useState<UsageOverview | null>(null);
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  // ❌ REMOVED: quickStats state and QuickStats interface
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const userData = localStorage.getItem("user");
      if (userData) {
        setUser(JSON.parse(userData));
      }

      await Promise.all([
        loadUsageOverview(),
        loadRecentActivity()
        // ❌ REMOVED: loadQuickStats()
      ]);
    } catch (error) {
      console.error("Failed to load dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadUsageOverview = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/api/usage/current`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();
      if (data.success) {
        const services = data.data.services;
        setUsageOverview({
          planType: data.data.subscription.planType,
          apiRequests: {
            used: services.API_REQUESTS?.used || 0,
            limit: services.API_REQUESTS?.limit || null,
          },
          tokens: {
            used: services.TOKENS?.used || 0,
            limit: services.TOKENS?.limit || null,
          },
        });
      }
    } catch (error) {
      console.error("Failed to load usage overview:", error);
    }
  };

  const loadRecentActivity = async () => {
    // Mock recent activity - replace with actual API call
    const mockActivity: RecentActivity[] = [
      {
        id: '1',
        type: 'chat',
        title: 'New chat session started',
        description: 'Customer inquiry about product features',
        timestamp: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
        icon: '💬'
      },
      {
        id: '2',
        type: 'script',
        title: 'Widget deployed',
        description: 'E-commerce script activated on main site',
        timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(),
        icon: '⚡'
      },
      {
        id: '3',
        type: 'api',
        title: 'API key created',
        description: 'New production API key generated',
        timestamp: new Date(Date.now() - 1000 * 60 * 60 * 4).toISOString(),
        icon: '🔑'
      }
    ];
    setRecentActivity(mockActivity);
  };

  // ❌ REMOVED: loadQuickStats function completely

  const formatTimeAgo = (timestamp: string) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInMinutes = Math.floor((now.getTime() - time.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    } else if (diffInMinutes < 1440) {
      return `${Math.floor(diffInMinutes / 60)}h ago`;
    } else {
      return `${Math.floor(diffInMinutes / 1440)}d ago`;
    }
  };

  const getUsagePercentage = (used: number, limit: number | null): number => {
    if (limit === null) return 0;
    return Math.min((used / limit) * 100, 100);
  };

  const formatNumber = (num: number | null): string => {
    if (num === null) return "Unlimited";
    return num.toLocaleString();
  };

  const getUsageColor = (percentage: number): string => {
    if (percentage > 80) return "from-red-400 to-red-600";
    if (percentage > 60) return "from-yellow-400 to-orange-500";
    return "from-green-400 to-emerald-500";
  };

  const getCategoryIcon = (category: string) => {
    const icons: Record<string, string> = {
      'ECOMMERCE': '🛍️',
      'REAL_ESTATE': '🏠',
      'ERP': '📋',
      'IP_REGISTRATION': '📝'
    };
    return icons[category] || '💼';
  };

  if (loading) {
    return (
      <DashboardLayout currentPage="Dashboard">
        <div className="flex items-center justify-center min-h-screen p-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full mx-auto mb-4 animate-pulse flex items-center justify-center">
              <span className="text-white text-2xl">🚀</span>
            </div>
            <div className="text-gray-600 font-medium">Loading your dashboard...</div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout currentPage="Dashboard">
      {/* Start content immediately at the top */}
      <div className="min-h-screen">
        {/* Page Header with Actions */}
        <div className="bg-white/80 backdrop-blur-lg border-b border-white/20 p-4 md:p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                <span className="text-white text-2xl">🏠</span>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  Dashboard
                </h1>
                <p className="text-gray-600 text-sm">
                  Overview of your AI assistant platform
                </p>
              </div>
            </div>
            
            {/* Quick Actions */}
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2 bg-white/60 rounded-full px-3 py-1 border border-gray-200">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-xs font-medium text-gray-600">Online</span>
              </div>
              <Link
                to="/chat"
                className="px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors text-sm font-medium"
              >
                💬 New Chat
              </Link>
              <Link
                to="/script-generator"
                className="px-4 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors text-sm font-medium"
              >
                ⚡ Create Script
              </Link>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="p-4 md:p-6 lg:p-8 space-y-6">
          {/* Welcome Section */}
          <div className="bg-white/70 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-500 to-purple-600 px-6 py-6 md:py-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-center space-x-4">
                  <div className="w-14 h-14 md:w-16 md:h-16 bg-white/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                    {user?.avatar ? (
                      <img 
                        src={user.avatar} 
                        alt="Profile" 
                        className="w-full h-full rounded-2xl object-cover"
                      />
                    ) : (
                      <span className="text-white text-xl md:text-2xl font-bold">
                        {user?.name ? user.name.charAt(0).toUpperCase() : user?.email.charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-2xl md:text-3xl font-bold text-white leading-tight">
                      Welcome back, {user?.name || user?.email.split("@")[0]}! 👋
                    </h2>
                    <p className="text-indigo-100 mt-1 text-sm md:text-base">
                      {getCategoryIcon(user?.category || '')} {user?.category?.replace('_', ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())} Dashboard
                    </p>
                  </div>
                </div>
                <div className="text-left md:text-right">
                  <p className="text-white/80 text-sm">Today</p>
                  <p className="text-white font-semibold">{new Date().toLocaleDateString()}</p>
                </div>
              </div>
            </div>

            {/* ✅ REPLACED: Old hardcoded Quick Stats with new Status Cards */}
            <div className="p-4 md:p-6 bg-gradient-to-r from-gray-50 to-white">
              <StatusCards user={user} usageOverview={usageOverview} />
            </div>
          </div>

          {/* ✅ NEW: Usage Insights Section */}
          {usageOverview && (
            <div className="bg-white/70 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 overflow-hidden">
              <div className="bg-gradient-to-r from-purple-500 to-pink-600 px-6 py-4">
                <h3 className="text-lg font-semibold text-white flex items-center">
                  <span className="mr-2">📊</span>
                  Usage Insights - Real-time Data
                </h3>
              </div>
              <div className="p-4 md:p-6">
                <UsageInsights usageOverview={usageOverview} />
              </div>
            </div>
          )}

          {/* Rest of your dashboard content... */}
          {/* Usage Overview */}
          {usageOverview && (
            <div className="bg-white/70 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 overflow-hidden">
              <div className="bg-gradient-to-r from-blue-500 to-cyan-600 px-6 py-4">
                <h3 className="text-lg font-semibold text-white flex items-center">
                  <span className="mr-2">📊</span>
                  Usage Overview - {usageOverview.planType} Plan
                </h3>
              </div>
              <div className="p-4 md:p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                  {/* API Requests Usage */}
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100">
                    <div className="flex justify-between items-center mb-3">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg flex items-center justify-center mr-3">
                          <span className="text-white text-sm">🔗</span>
                        </div>
                        <span className="font-semibold text-gray-800 text-sm md:text-base">API Requests</span>
                      </div>
                      <span className="text-xs bg-white px-2 py-1 rounded-full text-gray-600 font-medium">
                        {formatNumber(usageOverview.apiRequests.used)} / {formatNumber(usageOverview.apiRequests.limit)}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                      <div
                        className={`h-3 rounded-full bg-gradient-to-r ${getUsageColor(
                          getUsagePercentage(usageOverview.apiRequests.used, usageOverview.apiRequests.limit)
                        )} transition-all duration-500`}
                        style={{
                          width: `${getUsagePercentage(usageOverview.apiRequests.used, usageOverview.apiRequests.limit)}%`,
                        }}
                      />
                    </div>
                    <div className="mt-2 text-right">
                      <span className="text-sm font-medium text-gray-600">
                        {getUsagePercentage(usageOverview.apiRequests.used, usageOverview.apiRequests.limit).toFixed(1)}% used
                      </span>
                    </div>
                  </div>

                  {/* Tokens Usage */}
                  <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-4 border border-emerald-100">
                    <div className="flex justify-between items-center mb-3">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-gradient-to-r from-emerald-500 to-green-600 rounded-lg flex items-center justify-center mr-3">
                          <span className="text-white text-sm">🎯</span>
                        </div>
                        <span className="font-semibold text-gray-800 text-sm md:text-base">Tokens</span>
                      </div>
                      <span className="text-xs bg-white px-2 py-1 rounded-full text-gray-600 font-medium">
                        {formatNumber(usageOverview.tokens.used)} / {formatNumber(usageOverview.tokens.limit)}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                      <div
                        className={`h-3 rounded-full bg-gradient-to-r ${getUsageColor(
                          getUsagePercentage(usageOverview.tokens.used, usageOverview.tokens.limit)
                        )} transition-all duration-500`}
                        style={{
                          width: `${getUsagePercentage(usageOverview.tokens.used, usageOverview.tokens.limit)}%`,
                        }}
                      />
                    </div>
                    <div className="mt-2 text-right">
                      <span className="text-sm font-medium text-gray-600">
                        {getUsagePercentage(usageOverview.tokens.used, usageOverview.tokens.limit).toFixed(1)}% used
                      </span>
                    </div>
                  </div>
                </div>
                <div className="mt-6 text-center">
                  <Link
                    to="/usage"
                    className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg hover:from-indigo-600 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl font-medium text-sm md:text-base"
                  >
                    <span className="mr-2">📈</span>
                    View Detailed Analytics
                  </Link>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Actions */}
            <div className="lg:col-span-2 space-y-6">
              {/* Quick Actions Grid */}
              <div className="bg-white/70 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 overflow-hidden">
                <div className="bg-gradient-to-r from-orange-500 to-red-600 px-6 py-4">
                  <h3 className="text-lg font-semibold text-white">🚀 Quick Actions</h3>
                </div>
                <div className="p-4 md:p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Link
                      to="/chat"
                      className="group relative overflow-hidden bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl p-4 md:p-6 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                    >
                      <div className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-colors duration-300"></div>
                      <div className="relative z-10">
                        <div className="text-2xl md:text-3xl mb-2 md:mb-3">💬</div>
                        <h4 className="text-lg md:text-xl font-bold mb-1 md:mb-2">Start AI Chat</h4>
                        <p className="text-indigo-100 text-xs md:text-sm">Chat with your intelligent assistant</p>
                      </div>
                    </Link>

                    <Link
                      to="/script-generator"
                      className="group relative overflow-hidden bg-gradient-to-br from-orange-500 to-red-500 rounded-xl p-4 md:p-6 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                    >
                      <div className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-colors duration-300"></div>
                      <div className="relative z-10">
                        <div className="text-2xl md:text-3xl mb-2 md:mb-3">⚡</div>
                        <h4 className="text-lg md:text-xl font-bold mb-1 md:mb-2">Create Widget</h4>
                        <p className="text-orange-100 text-xs md:text-sm">Generate embeddable chat scripts</p>
                      </div>
                    </Link>

                    {/* <Link
                      to="/api-keys"
                      className="group relative overflow-hidden bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl p-4 md:p-6 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                    >
                      <div className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-colors duration-300"></div>
                      <div className="relative z-10">
                        <div className="text-2xl md:text-3xl mb-2 md:mb-3">🔑</div>
                        <h4 className="text-lg md:text-xl font-bold mb-1 md:mb-2">API Keys</h4>
                        <p className="text-blue-100 text-xs md:text-sm">Manage your API access</p>
                      </div>
                    </Link> */}
                    <Link
               to="/usage"
               className="group relative overflow-hidden bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl p-6 text-white shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1"
             >
              <div className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-colors duration-300"></div>
               <div className="relative z-10">
                 <div className="flex items-center mb-4">
                   <div className="text-3xl mr-3">📊</div>
                   <div>
                     <h3 className="text-xl font-bold">Usage Analytics</h3>
                     <p className="text-green-100 text-sm">
                       View usage statistics
                     </p>
                   </div>
                 </div>
                 <div className="text-right">
                   <span className="text-white/80 text-xs font-medium">
                     View Stats →
                   </span>
                 </div>
               </div>
             </Link>

                    <Link
                      to="/subscription"
                      className="group relative overflow-hidden bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl p-4 md:p-6 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                    >
                      <div className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-colors duration-300"></div>
                      <div className="relative z-10">
                        <div className="text-2xl md:text-3xl mb-2 md:mb-3">💳</div>
                        <h4 className="text-lg md:text-xl font-bold mb-1 md:mb-2">Subscription</h4>
                        <p className="text-purple-100 text-xs md:text-sm">Manage your plan</p>
                      </div>
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Activity Sidebar */}
            <div className="space-y-6">
              {/* Recent Activity */}
              <div className="bg-white/70 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 overflow-hidden">
                <div className="bg-gradient-to-r from-green-500 to-emerald-600 px-6 py-4">
                  <h3 className="text-lg font-semibold text-white">📋 Recent Activity</h3>
                </div>
                <div className="p-4">
                  {recentActivity.length > 0 ? (
                    <div className="space-y-3">
                      {recentActivity.slice(0, 5).map((activity) => (
                        <div key={activity.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                          <div className="w-8 h-8 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                            <span className="text-white text-sm">{activity.icon}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{activity.title}</p>
                            <p className="text-xs text-gray-600 truncate">{activity.description}</p>
                            <p className="text-xs text-gray-500 mt-1">{formatTimeAgo(activity.timestamp)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <div className="text-4xl mb-2">📭</div>
                      <p className="text-sm">No recent activity</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Account Status */}
              <div className="bg-white/70 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 overflow-hidden">
                <div className="bg-gradient-to-r from-green-500 to-emerald-600 px-6 py-4">
                  <h3 className="text-lg font-semibold text-white">✅ Account Status</h3>
                </div>
                <div className="p-4 space-y-3">
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <span className="text-green-600">📧</span>
                      <span className="text-sm font-medium text-gray-800">Email</span>
                    </div>
                    <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                      {user?.isVerified ? 'Verified' : 'Pending'}
                    </span>
                  </div>

                  {usageOverview && (
                    <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <span className="text-blue-600">💎</span>
                        <span className="text-sm font-medium text-gray-800">Plan</span>
                      </div>
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                        {usageOverview.planType}
                      </span>
                    </div>
                  )}

                  <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <span className="text-purple-600">🔒</span>
                      <span className="text-sm font-medium text-gray-800">Security</span>
                    </div>
                    <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-full">
                      Protected
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;